import React from "react";
import { CompanyType } from "../../../../__generated__/graphql";

// Types for cube.js data
export interface AggregateTdData {
  "aggregateTdLevel.totalCost"?: number;
  "aggregateTdLevel.discountConcrete"?: number;
  "aggregateTdLevel.platformfeeConcrete"?: number;
  "aggregateTdLevel.reads"?: number;
  "aggregateTdLevel.publisherId"?: string;
  "aggregateTdLevel.websiteId"?: string;
  "aggregateTdLevel.campaignId"?: string;
  "aggregateTdLevel.campaignName"?: string;
  "aggregateTdLevel.websiteName"?: string;
  "aggregateTdLevel.publisherName"?: string;
  "aggregateTdLevel.advertiserId"?: string;
  [key: string]: any;
}

export interface ModifiedCostData {
  key?: string;
  Cost?: number;
  discountConcrete?: number;
  reads?: number;
  publisherId?: string;
  websiteId?: string;
  campaignId?: string;
  advertiserId?: string;
  campaignName: string;
  websiteName?: string;
  publisherName?: string;
}

// We're not creating a full type definition for ResultSet as it's complex
// Just defining the structure we need for our component
export interface CubeQueryResult {
  tablePivot?: AggregateTdData[];
  rawData?: () => AggregateTdData[];
}

// Component props interfaces
export interface AgencyCostProps {
  // Can be extended if needed
}

export interface AdvertiserCostTableProps {
  queryStart: string;
  queryEnd: string;
  user: {
    company: {
      type: CompanyType;
    };
  };
}

/**
 * Transform cube.js data into a format suitable for display in the table
 * @param data Raw data from the cube.js query
 * @returns Processed data ready for the table component
 */
export const processCubeData = (data: AggregateTdData[] | undefined): ModifiedCostData[] => {
  if (!data || data.length === 0) {
    return [];
  }

  return data.map((item) => {
    // Handle null/undefined item
    if (!item) return {
      campaignName: "Unknown Campaign",
    };

    const newItem: Record<string, any> = {};
    
    Object.entries(item).forEach(([key, value]) => {
      const newKey = key.replace("aggregateTdLevel.", "").replace("total", "");
      newItem[newKey] = value;
    });


    return {
      ...newItem,
      campaignName: newItem.campaignName ?? "Unknown Campaign",

    };
  });
};

/**
 * Create cube.js query object for cost data
 * @param queryStart Start date in YYYY-MM-DD format
 * @param queryEnd End date in YYYY-MM-DD format
 * @returns Query object for cube.js client
 */
export const createCostQuery = (queryStart: string, queryEnd: string) => {
  return {
    order: {
      "aggregateTdLevel.totalCost": "desc",
    },
    measures: [
      "aggregateTdLevel.totalCost",
      "aggregateTdLevel.reads",
    ],
    dimensions: [
      "aggregateTdLevel.campaignId",
      "aggregateTdLevel.advertiserId",
      "aggregateTdLevel.campaignName",
    ],
    filters: [
      {
        member: "aggregateTdLevel.fraud",
        operator: "notEquals",
        values: ["true"],
      },
    ],
    timeDimensions: [
      {
        dimension: "aggregateTdLevel.date",
        dateRange: [queryStart, queryEnd] as [string, string], // Type assertion to fix the error
      },
    ],
  };
};

/**
 * Generate table columns configuration
 * @returns Column configuration for the table component
 */
export const getCostTableColumns = () => {
  return [
    {
      title: "Campaign",
      dataIndex: "campaignName",
      key: "campaignId",
      render: (text: string, record: ModifiedCostData) =>
        record.key === "total" ? <strong>TOTAL</strong> : record.campaignName,
    },
    {
      title: "Reads",
      dataIndex: "reads",
      key: "reads",
      render: (text: number, record: ModifiedCostData) =>
        record.key === "total" ? (
          <strong>{parseFloat(String(text)).toFixed(0)}</strong>
        ) : (
          parseFloat(String(text)).toFixed(0)
        ),
    },
    {
      title: "Gross Cost",
      dataIndex: "Cost",
      key: "Cost",
      render: (text: number, record: ModifiedCostData) =>
        record.key === "total" ? (
          <strong>{parseFloat(String(text)).toFixed(1)}</strong>
        ) : (
          parseFloat(String(text)).toFixed(1)
        ),
    },
  ];
};

/**
 * Export cost data to Excel
 * @param tableData Processed cost data
 * @param workbookName Name of the Excel file to create
 */
export const exportToExcelRevenue = (tableData: ModifiedCostData[], workbookName: string): void => {
  // Import XLSX and saveAs from file-saver when this function is used
  const XLSX = require('xlsx');
  const { saveAs } = require('file-saver');
  
  // Create a new workbook
  const workbook = XLSX.utils.book_new();

  // Add data to worksheet
  const campaignWorksheet = XLSX.utils.json_to_sheet(tableData);
  XLSX.utils.book_append_sheet(workbook, campaignWorksheet, `Revenue Data`);

  // Write the workbook to a buffer
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const data = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  });

  // Save the Excel file
  saveAs(data, `${workbookName}.xlsx`);
};
